import React, { useState } from 'react';
import { useTranslation } from '../../context/LanguageContext';
import {
    X, Briefcase, User, MapPin, Calendar, Clock, Mail, Phone, Building2,
    Edit2, Trash2, CheckCircle, AlertCircle, Loader2
} from 'lucide-react';
import {
    getTypeColor, getTypeLabel, getUrgencyColor, getUrgencyLabel,
    getCompensationColor, getCompensationLabel, getDurationColor, getDurationLabel,
    deleteRequest, closeRequest
} from '../../services/workRequestsService';
import { getStoredUser as getUser } from '../../context/AuthContext';

const WorkRequestDetail = ({ request, onClose, onUpdated }) => {
    const { t } = useTranslation();
    const user = getUser();
    const isOwner = user?.id === request.peopleid;

    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [showConfirmClose, setShowConfirmClose] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleDelete = async () => {
        setLoading(true);
        setError('');
        const result = await deleteRequest(request.id, user?.id);
        setLoading(false);

        if (result.success) {
            onUpdated();
        } else {
            setError(result.message || t.work?.errors?.deleteFailed || 'Erreur lors de la suppression');
        }
    };

    const handleClose = async () => {
        setLoading(true);
        setError('');
        const result = await closeRequest(request.id, user?.id);
        setLoading(false);

        if (result.success) {
            onUpdated();
        } else {
            setError(result.message || t.work?.errors?.closeFailed || 'Erreur lors de la fermeture');
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div
                    className="flex items-center justify-between p-6 text-white"
                    style={{ backgroundColor: getTypeColor(request.requesttype) }}
                >
                    <div className="flex items-center gap-3">
                        <Briefcase className="w-6 h-6" />
                        <div>
                            <span className="text-sm opacity-80">
                                {getTypeLabel(request.requesttype)}
                            </span>
                            <h2 className="text-xl font-semibold">{request.title}</h2>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-white/20 rounded-full transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
                    {error && (
                        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700">
                            <AlertCircle className="w-5 h-5 flex-shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}

                    {/* Status Badge */}
                    {request.status !== 'active' && (
                        <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
                            request.status === 'closed'
                                ? 'bg-gray-100 text-gray-600'
                                : 'bg-yellow-50 text-yellow-700'
                        }`}>
                            <CheckCircle className="w-5 h-5" />
                            {request.status === 'closed'
                                ? (t.work?.statusClosed || 'Cette demande est clôturée')
                                : (t.work?.statusExpired || 'Cette demande a expiré')
                            }
                        </div>
                    )}

                    {/* Badges Row */}
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                        {/* Urgency Badge */}
                        <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4 text-gray-400" />
                            <span
                                className="px-3 py-1 rounded-full text-sm font-medium text-white"
                                style={{ backgroundColor: getUrgencyColor(request.urgencybadge) }}
                            >
                                {getUrgencyLabel(request.urgencybadge)}
                            </span>
                        </div>
                        {/* Compensation Badge */}
                        {request.compensation && (
                            <span
                                className="px-3 py-1 rounded-full text-sm font-medium text-white"
                                style={{ backgroundColor: getCompensationColor(request.compensation) }}
                            >
                                {getCompensationLabel(request.compensation)}
                            </span>
                        )}
                        {/* Duration Badge */}
                        {request.duration && (
                            <span
                                className="px-3 py-1 rounded-full text-sm font-medium text-white"
                                style={{ backgroundColor: getDurationColor(request.duration) }}
                            >
                                {getDurationLabel(request.duration)}
                            </span>
                        )}
                    </div>

                    {/* Description */}
                    <div className="mb-6">
                        <h3 className="text-sm font-medium text-gray-500 mb-2">
                            {t.work?.detail?.description || 'Description'}
                        </h3>
                        <p className="text-gray-700 whitespace-pre-wrap">
                            {request.description}
                        </p>
                    </div>

                    {/* Skills */}
                    {request.skillsneeded && (
                        <div className="mb-6">
                            <h3 className="text-sm font-medium text-gray-500 mb-2">
                                {t.work?.detail?.skills || 'Compétences recherchées'}
                            </h3>
                            <div className="flex flex-wrap gap-2">
                                {request.skillsneeded.split(',').map((skill, idx) => (
                                    <span
                                        key={idx}
                                        className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm"
                                    >
                                        {skill.trim()}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Location */}
                    {request.location && (
                        <div className="mb-6">
                            <h3 className="text-sm font-medium text-gray-500 mb-2">
                                {t.work?.detail?.location || 'Lieu'}
                            </h3>
                            <div className="flex items-center gap-2 text-gray-700">
                                <MapPin className="w-4 h-4" />
                                {request.location}
                            </div>
                        </div>
                    )}

                    {/* Creator Info */}
                    <div className="border-t pt-6">
                        <h3 className="text-sm font-medium text-gray-500 mb-3">
                            {t.work?.detail?.postedBy || 'Publié par'}
                        </h3>
                        <div className="flex items-start gap-4">
                            {request.creatorphotourl ? (
                                <img
                                    src={request.creatorphotourl}
                                    alt=""
                                    className="w-14 h-14 rounded-full object-cover"
                                />
                            ) : (
                                <div className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center">
                                    <User className="w-7 h-7 text-gray-400" />
                                </div>
                            )}
                            <div className="flex-1">
                                <p className="font-medium text-gray-800">
                                    {request.creatorfirstname} {request.creatorlastname}
                                </p>
                                {request.creatorcompany && (
                                    <p className="flex items-center gap-1 text-sm text-gray-500 mt-1">
                                        <Building2 className="w-4 h-4" />
                                        {request.creatorcompany}
                                    </p>
                                )}
                                {request.creatoremail && !isOwner && (
                                    <a
                                        href={`mailto:${request.creatoremail}`}
                                        className="flex items-center gap-1 text-sm text-blue-500 hover:text-blue-600 mt-1"
                                    >
                                        <Mail className="w-4 h-4" />
                                        {request.creatoremail}
                                    </a>
                                )}
                                {request.creatorphone && !isOwner && (
                                    <a
                                        href={`tel:${request.creatorphone}`}
                                        className="flex items-center gap-1 text-sm text-blue-500 hover:text-blue-600 mt-1"
                                    >
                                        <Phone className="w-4 h-4" />
                                        {request.creatorphone}
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Dates */}
                    <div className="mt-6 pt-4 border-t text-sm text-gray-400">
                        <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            {t.work?.detail?.createdAt || 'Publié le'} {formatDate(request.createdat)}
                        </div>
                        {request.closedat && (
                            <div className="flex items-center gap-2 mt-1">
                                <CheckCircle className="w-4 h-4" />
                                {t.work?.detail?.closedAt || 'Clôturé le'} {formatDate(request.closedat)}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer - Owner Actions */}
                {isOwner && request.status === 'active' && (
                    <div className="flex items-center justify-between p-6 border-t bg-gray-50">
                        <button
                            onClick={() => setShowConfirmDelete(true)}
                            className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                            <Trash2 className="w-4 h-4" />
                            {t.work?.actions?.delete || 'Supprimer'}
                        </button>
                        <button
                            onClick={() => setShowConfirmClose(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                        >
                            <CheckCircle className="w-4 h-4" />
                            {t.work?.actions?.close || 'Marquer comme trouvé'}
                        </button>
                    </div>
                )}

                {/* Not owner - Contact */}
                {!isOwner && request.status === 'active' && (
                    <div className="flex items-center justify-center p-6 border-t bg-gray-50">
                        {request.creatoremail ? (
                            <a
                                href={`mailto:${request.creatoremail}?subject=${encodeURIComponent((t.work?.contactSubject || 'Réponse à votre demande: ') + request.title)}`}
                                className="flex items-center gap-2 px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                            >
                                <Mail className="w-4 h-4" />
                                {t.work?.actions?.contact || 'Contacter'}
                            </a>
                        ) : (
                            <span className="text-gray-400">
                                {t.work?.noContactInfo || 'Aucune information de contact disponible'}
                            </span>
                        )}
                    </div>
                )}
            </div>

            {/* Confirm Delete Dialog */}
            {showConfirmDelete && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-60 p-4">
                    <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full">
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">
                            {t.work?.confirm?.deleteTitle || 'Supprimer cette demande ?'}
                        </h3>
                        <p className="text-gray-600 mb-4">
                            {t.work?.confirm?.deleteMessage || 'Cette action est irréversible.'}
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setShowConfirmDelete(false)}
                                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                            >
                                {t.common?.cancel || 'Annuler'}
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={loading}
                                className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
                            >
                                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                                {t.common?.delete || 'Supprimer'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirm Close Dialog */}
            {showConfirmClose && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-60 p-4">
                    <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full">
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">
                            {t.work?.confirm?.closeTitle || 'Clôturer cette demande ?'}
                        </h3>
                        <p className="text-gray-600 mb-4">
                            {t.work?.confirm?.closeMessage || 'La demande ne sera plus visible publiquement.'}
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setShowConfirmClose(false)}
                                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                            >
                                {t.common?.cancel || 'Annuler'}
                            </button>
                            <button
                                onClick={handleClose}
                                disabled={loading}
                                className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
                            >
                                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                                {t.work?.actions?.close || 'Clôturer'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WorkRequestDetail;
